import { useEffect, useRef, useState } from "react";
import {
  kRallyOutcomeForcingError,
  kRallyOutcomeNetcord,
  kRallyOutcomeNetted,
  kRallyOutcomeNettedPassingShot,
  kRallyOutcomeOut,
  kRallyOutcomeOutOffNet,
  kRallyOutcomeOutPassingShot,
  kRallyOutcomePassingShot,
  kRallyOutcomePutAway,
  kRallyOutcomeUnforcedError,
  kRallyOutcomeWinner,
  kReturnForcingError,
  kReturning,
  kReturnNetted,
  kReturnOut,
  kReturnUnforcedError,
  kReturnWinner,
  kServeAce,
  kServeFootfault,
  kServeNet,
  kServeOut,
  kServeOutOffNet,
  kServeWinner,
  kServing,
  kStageFirstReturnAd,
  kStageFirstReturnDeuce,
  kStageFirstServeAd,
  kStageFirstServeDeuce,
  kStageRally,
  kStageSecondReturnAd,
  kStageSecondReturnDeuce,
  kStageSecondServeAd,
  kStageSecondServeDeuce
} from "../../utils/pstsDefines";
import { eventString } from "../../utils/pstsFile";
import { drawArrowHead } from "../../utils/utils";

// ── Draw-from rules (tune these) ─────────────────────────────────────────────
// Everything is expressed in the TS-normalised court space (0–100 on both
// axes) where the net is the centre line at y = 50, so it works for either
// court orientation.
const CODING_RULES = {
  // Return/rally shots must start inside a cone that opens *backwards* along the
  // direction the previous shot travelled, anchored at where it landed. This is
  // the HALF angle, so the full cone spans 2× this. 45 → a 90° quadrant.
  rallyStartHalfAngleDeg: 45,
  // Serve start box: this deep (metres) behind the server's baseline, spanning
  // the centre line to the singles sideline on the correct half.
  serveStartDepthM: 1,
  // Grow the serve target box by this (metres) on all sides so serves that land
  // out / in the net can be recorded with a single tap.
  serveTargetExtendM: 1,
  // When a serve is recorded by tapping the target box, the serve line is drawn
  // from an assumed origin this far behind the baseline and 1 m from the centre.
  serveTapStartBehindM: 0.5,
  // How close to the net (TS units from the centre line at y = 50) counts as a
  // net / netted shot.
  netProximity: 6,
  // Reject draws that don't start in the valid region.
  enforceStartZone: true,
  // Highlight the valid start region with transparency so it can be seen/tuned.
  debugDrawStartZone: true,
};

// Court geometry. TS space is 0–100 on each axis, so the axes have different
// metre scales. The singles court spans x = 13 … 87 with the centre line at 50.
const COURT_LENGTH_M = 23.77;
const COURT_WIDTH_M = 10.97;
const SINGLES_LEFT_X = 13;
const SINGLES_RIGHT_X = 87;
const CENTER_X = 50;
const metresToTsY = (m) => (m / COURT_LENGTH_M) * 100;
const metresToTsX = (m) => (m / COURT_WIDTH_M) * 100;

export default function CodingCourt({
  isVertical,
  courtSize,
  topPlayer,
  bottomPlayer,
  currentPlayer,
  currentStage,
  currentSide,
  pointNumber,
  selectedEvent,
  isEditMode,
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent,
  onToggleHand,
  onToggleSpin,
  updated,
}) {
  const canvas = useRef();
  const ref = useRef(null);
  // Remembers the last placed click (position + time) to ignore an accidental
  // second click landing near the first one.
  const lastClickRef = useRef(null);
  // Time of the last touch. iPadOS fires emulated mouse events after touch
  // events (a pen/finger tap = touch* + mouse*), which would double-register a
  // shot; we ignore mouse events that closely follow a touch.
  const lastTouchRef = useRef(0);

  const [isDrawing, setIsDrawing] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [canvasHeight, setCanvasHeight] = useState(0);
  const [start, setStart] = useState({ x: 0, y: 0 });
  const [end, setEnd] = useState({ x: 0, y: 0 });
  const [courtArea, setCourtArea] = useState(null);
  const [isBallIn, setIsBallIn] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentState, setCurrentState] = useState({
    stage: kStageFirstServeDeuce,
  });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const drawCourtLines = (ctx, ca) => {
    ctx.beginPath();

    // ctx.fillStyle = '#0044ff';
    // ctx.fillRect(ca.left, ca.top, ca.width, ca.height);

    ctx.lineWidth = ca.scale * 2;
    ctx.strokeStyle = "#ffffff";
    ctx.strokeRect(ca.left, ca.top, ca.width, ca.height);

    if (isVertical) {
      ctx.moveTo(ca.left - ca.netPost, ca.top + ca.height / 2);
      ctx.lineTo(ca.left + ca.width + ca.netPost, ca.top + ca.height / 2);
      ctx.stroke();
    } else {
      ctx.moveTo(ca.left + ca.width / 2, ca.top - ca.netPost);
      ctx.lineTo(ca.left + ca.width / 2, ca.top + ca.height + ca.netPost);
      ctx.stroke();
    }

    ctx.fillStyle = "#ffffff";
    if (isVertical) {
      ctx.fillRect(
        ca.left - ca.netPost - ca.postWidth / 2,
        ca.top + ca.height / 2 - ca.postWidth / 2,
        ca.postWidth,
        ca.postWidth
      );
      ctx.fillRect(
        ca.left + ca.width + ca.netPost - ca.postWidth / 2,
        ca.top + ca.height / 2 - ca.postWidth / 2,
        ca.postWidth,
        ca.postWidth
      );
    } else {
      ctx.fillRect(
        ca.left + ca.width / 2 - ca.postWidth / 2,
        ca.top - ca.netPost - ca.postWidth / 2,
        ca.postWidth,
        ca.postWidth
      );
      ctx.fillRect(
        ca.left + ca.width / 2 - ca.postWidth / 2,
        ca.top + ca.height + ca.netPost - ca.postWidth / 2,
        ca.postWidth,
        ca.postWidth
      );
    }

    if (isVertical) {
      ctx.moveTo(ca.left + ca.tramWidth, ca.top);
      ctx.lineTo(ca.left + ca.tramWidth, ca.top + ca.height);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.width - ca.tramWidth, ca.top);
      ctx.lineTo(ca.left + ca.width - ca.tramWidth, ca.top + ca.height);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.tramWidth, ca.top + ca.serviceLine);
      ctx.lineTo(ca.left + ca.width - ca.tramWidth, ca.top + ca.serviceLine);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.tramWidth, ca.top + ca.height - ca.serviceLine);
      ctx.lineTo(
        ca.left + ca.width - ca.tramWidth,
        ca.top + ca.height - ca.serviceLine
      );
      ctx.stroke();
    } else {
      ctx.moveTo(ca.left, ca.top + ca.tramWidth);
      ctx.lineTo(ca.left + ca.width, ca.top + ca.tramWidth);
      ctx.stroke();

      ctx.moveTo(ca.left, ca.top + ca.height - ca.tramWidth);
      ctx.lineTo(ca.left + ca.width, ca.top + ca.height - ca.tramWidth);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.serviceLine, ca.top + ca.tramWidth);
      ctx.lineTo(ca.left + ca.serviceLine, ca.top + ca.height - ca.tramWidth);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.width - ca.serviceLine, ca.top + ca.tramWidth);
      ctx.lineTo(
        ca.left + ca.width - ca.serviceLine,
        ca.top + ca.height - ca.tramWidth
      );
      ctx.stroke();
    }

    if (isVertical) {
      ctx.moveTo(ca.left + ca.width / 2, ca.top + ca.serviceLine);
      ctx.lineTo(ca.left + ca.width / 2, ca.top + ca.height - ca.serviceLine);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.width / 2, ca.top);
      ctx.lineTo(ca.left + ca.width / 2, ca.top + 10 * ca.scale);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.width / 2, ca.top + ca.height - 10 * ca.scale);
      ctx.lineTo(ca.left + ca.width / 2, ca.top + ca.height);
      ctx.stroke();
    } else {
      ctx.moveTo(ca.left + ca.serviceLine, ca.top + ca.height / 2);
      ctx.lineTo(ca.left + ca.width - ca.serviceLine, ca.top + ca.height / 2);
      ctx.stroke();

      ctx.moveTo(ca.left, ca.top + ca.height / 2);
      ctx.lineTo(ca.left + 10 * ca.scale, ca.top + ca.height / 2);
      ctx.stroke();

      ctx.moveTo(ca.left + ca.width - 10 * ca.scale, ca.top + ca.height / 2);
      ctx.lineTo(ca.left + ca.width, ca.top + ca.height / 2);
      ctx.stroke();
    }
  };

  const stageText = (stage) => {
    switch (stage) {
      case kStageFirstServeDeuce:
        return "1st Serve Deuce";
      case kStageFirstServeAd:
        return "1st Serve Ad";
      case kStageSecondServeDeuce:
        return "2nd Serve Deuce";
      case kStageSecondServeAd:
        return "2nd Serve Ad";
      case kStageFirstReturnDeuce:
        return "1st Return Deuce";
      case kStageFirstReturnAd:
        return "1st Return Ad";

      case kStageSecondReturnDeuce:
        return "2nd Return Deuce";
      case kStageSecondReturnAd:
        return "2nd Return Ad";
      case kStageRally:
        return "Rally";
      default:
        return "Unknown";
    }
  };

  const drawCourt = (ctx, ca) => {
    // Edit mode (reviewing/correcting a past shot from the events list) uses a
    // visibly different, more muted court colour so it's never confused with
    // live coding mode.
    ctx.fillStyle = isEditMode ? "#2e3a59" : "#0044ff";
    ctx.fillRect(ca.left, ca.top, ca.width, ca.height);

    // The serve/rally target zone only means something for the shot about to
    // be coded live — it doesn't apply to a past shot being reviewed/edited.
    const tz = isEditMode ? null : targetZone(ca);
    if (tz) {
      if (isVertical) {
        const xx = ca.width / (100 * ca.scale);
        const yy = ca.height / (100 * ca.scale);
        ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
        ctx.fillRect(
          ca.left + tz.x * xx * ca.scale,
          ca.top + tz.y * yy * ca.scale,
          tz.w * xx * ca.scale,
          tz.h * yy * ca.scale
        );
      } else {
        const xx = ca.height / (100 * ca.scale);
        const yy = ca.width / (100 * ca.scale);
        ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
        ctx.fillRect(
          ca.left + tz.y * yy * ca.scale,
          ca.top + tz.x * xx * ca.scale, // - tz.w * xx * ca.scale,
          tz.h * yy * ca.scale,
          tz.w * xx * ca.scale
        );
      }
    }

    drawCourtLines(ctx, ca);
    const y = isVertical ? 30 * ca.scale : 20 * ca.scale;
    drawPlayerName(ctx, ca, topPlayer, 10, y);
    const xx = isVertical ? 20 * ca.scale : 10 * ca.scale;
    const pl2y = isVertical ? courtSize.height - xx : courtSize.width - xx;
    drawPlayerName(ctx, ca, bottomPlayer, 10, pl2y);
    if (!currentPlayer || !topPlayer) return;
    const offset = isVertical ? 40 * ca.scale : 40 * ca.scale;
    const yy = isVertical ? 0 * ca.scale : 10 * ca.scale;
    const ystage = isVertical
      ? currentPlayer.guid === topPlayer.guid
        ? y + offset - yy
        : courtSize.height - offset - 10 * ca.scale + yy
      : currentPlayer.guid === topPlayer.guid
      ? y + offset - yy
      : courtSize.width - offset - 10 * ca.scale + yy;

    drawStage(ctx, ca, currentPlayer, 10, ystage);
  };

  const calculateCourtDimensions = () => {
    setCanvasWidth(courtSize.width);
    setCanvasHeight(courtSize.height);
    var xmargin = isVertical ? 20 : 100;
    var ymargin = isVertical ? 100 : 20;
    var baseWidth = isVertical ? 216 : 468;
    var baseHeight = isVertical ? 468 : 216;
    var tramWidth = 27;
    var serviceLine = 108;
    // var serveZoneDepth = 40;
    var netPost = 18;
    var postWidth = 8;

    // var scalew = ref.current.clientWidth / (baseWidth + xmargin * 2);
    // var scaleh = ref.current.clientHeight / (baseHeight + ymargin * 2);
    var scalew = courtSize.width / (baseWidth + xmargin * 2);
    var scaleh = courtSize.height / (baseHeight + ymargin * 2);
    var scale = scaleh < scalew ? scaleh : scalew;
    // var scale = scaleh < scalew ? scaleh * 2 : scalew * 2;
    // console.log('scalew sclaeh scale', scalew, scaleh, scale)

    xmargin = (courtSize.width - (baseWidth + tramWidth * 2) * scale) / 2;
    ymargin = (courtSize.height - baseHeight * scale) / 2;

    baseWidth = baseWidth * scale;
    baseHeight = baseHeight * scale;
    tramWidth = tramWidth * scale;
    serviceLine = serviceLine * scale;
    // serveZoneDepth = serveZoneDepth * scale
    netPost = netPost * scale;
    postWidth = postWidth * scale;

    xmargin = (ref.current.clientWidth - baseWidth) / 2;
    ymargin = (ref.current.clientHeight - baseHeight) / 2;

    var zones = {};
    if (isVertical) {
      zones["deuceServeBottom"] = { x: 50, y: 50, w: 37, h: 27 };
      zones["adServeBottom"] = { x: 13, y: 50, w: 37, h: 27 };
      zones["rallyBottom"] = { x: 13, y: 50, w: 74, h: 50 };
      zones["deuceServeTop"] = { x: 13, y: 23, w: 37, h: 27 };
      zones["adServeTop"] = { x: 50, y: 23, w: 37, h: 27 };
      zones["rallyTop"] = { x: 13, y: 0, w: 74, h: 50 };
    } else {
      zones["deuceServeRight"] = { x: 13, y: 50, w: 37, h: 27 };
      zones["adServeRight"] = { x: 50, y: 50, w: 37, h: 27 };
      zones["rallyRight"] = { x: 13, y: 50, w: 74, h: 50 };
      zones["deuceServeLeft"] = { x: 50, y: 23, w: 37, h: 27 };
      zones["adServeLeft"] = { x: 13, y: 23, w: 37, h: 27 };
      zones["rallyLeft"] = { x: 13, y: 0, w: 74, h: 50 };
    }
    const ca = {
      left: xmargin,
      top: ymargin,
      width: baseWidth,
      height: baseHeight,
      netPost: netPost,
      postWidth: postWidth,
      tramWidth: tramWidth,
      serviceLine: serviceLine,
      scale: scale,
      isVertical: isVertical,
      zones: zones,
    };
    setCourtArea(ca);
    return ca;
  };

  const drawPlayerName = (ctx, ca, player, x, y) => {
    if (!player || !currentPlayer) return;
    // In edit mode, highlight whoever hit the selected (past) shot instead of
    // the live current player.
    const highlightGuid =
      isEditMode && selectedEvent?.player
        ? selectedEvent.player.guid
        : currentPlayer.guid;
    if (player.guid === highlightGuid) {
      ctx.fillStyle = "darkmagenta";
    } else {
      ctx.fillStyle = "lightgray";
    }

    var fontSize = ca ? 14 * ca.scale : 14;
    if (fontSize < 18 * ca.scale) fontSize = 18 * ca.scale;
    else if (fontSize > 20 * ca.scale) fontSize = 20 * ca.scale;
    ctx.textAlign = "center";
    if (isVertical) {
      //   ctx.fillRect(x - 5, y - 16, courtSize.width - 10, 20);
      //   ctx.fillStyle = "#ffffff";
      ctx.font = `500 ${fontSize}px Arial`;
      ctx.fillText(
        player.firstName.substring(0, 1) + ". " + player.lastName.toUpperCase(),
        courtSize.width / 2,
        y
      );
    } else {
      ctx.save();
      // ctx.translate(x, y);
      ctx.rotate(-Math.PI / 2);
      ctx.font = `500 ${fontSize}px Arial`;
      ctx.fillText(
        player.firstName.substring(0, 1) + ". " + player.lastName.toUpperCase(),
        -courtSize.height / 2,
        y
      );
      ctx.restore();
    }
  };

  const drawStage = (ctx, ca, player, x, y) => {
    // In edit mode, show what the selected shot actually was (e.g. "1st Serve
    // - Net") instead of the live match-state label (e.g. "1ST SERVE DEUCE").
    const label =
      isEditMode && selectedEvent
        ? eventString(selectedEvent)
        : stageText(currentStage).toUpperCase();
    ctx.fillStyle = isEditMode ? "#ffd166" : "lightGray";

    var fontSize = ca ? 20 * ca.scale : 20;
    if (fontSize < 20 * ca.scale) fontSize = 20 * ca.scale;
    else if (fontSize > 24 * ca.scale) fontSize = 24 * ca.scale;
    ctx.textAlign = "center";
    if (isVertical) {
      //   ctx.fillRect(x - 5, y - 16, courtSize.width - 10, 20);
      //   ctx.fillStyle = "#ffffff";
      ctx.font = `900 ${fontSize}px Helvetica`;
      ctx.fillText(label, courtSize.width / 2, y);
    } else {
      ctx.save();
      //   ctx.translate(x, y);
      ctx.rotate(-Math.PI / 2);
      ctx.font = `900 ${fontSize}px Helvetica`;
      ctx.fillText(label, -courtSize.height / 2, y);
      ctx.restore();
    }
  };

  const isInZone = (pt, zone) => {
    if (!pt || !zone) return false;
    if (isVertical) {
      return (
        pt.x >= zone.x &&
        pt.x <= zone.x + zone.w &&
        pt.y >= zone.y &&
        pt.y <= zone.y + zone.h
      );
    } else {
      const y = 100 - pt.x;
      const x = pt.y;
      return (
        y >= zone.x &&
        y <= zone.x + zone.w &&
        x >= zone.y &&
        x <= zone.y + zone.h
      );
    }

    // if (isVertical) {
    //   return (
    //     pt.x >= zone.x &&
    //     pt.x <= zone.x + zone.w &&
    //     pt.y >= zone.y &&
    //     pt.y <= zone.y + zone.h
    //   );
    // } else {
    //   return (
    //     pt.y >= zone.x &&
    //     pt.y <= zone.x + zone.w &&
    //     pt.x >= zone.y &&
    //     pt.x <= zone.y + zone.h
    //   );
    // }
  };

  useEffect(() => {
    if (!canvas.current) return;

    // clear canvas
    const ctx = canvas.current.getContext("2d");
    ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);

    var ca = courtArea;
    if (
      !ca ||
      ca.isVertical !== isVertical ||
      courtSize.width !== canvasWidth ||
      courtSize.height !== canvasHeight
    ) {
      ca = calculateCourtDimensions();
      drawCourt(ctx, ca);
      return;
    } else {
      drawCourt(ctx, ca);
    }

    if (CODING_RULES.debugDrawStartZone && !isEditMode) drawStartZone(ctx, ca);

    drawSelectedEventPath(ctx, ca);

    const isIn = checkBallIsIn(end);

    var colour = isIn ? "#00ee00" : "#ff0000";
    if (isDrawing) {
      // draw the line
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(end.x, end.y);
      ctx.closePath();
      ctx.strokeStyle = colour;
      ctx.lineWidth = 5;
      ctx.stroke();
      drawArrowHead(ctx, start, end, 16, 0.3, colour, colour);
    } else {
      drawBallMark(ctx, start, end);
    }
  }, [isDrawing, start, end, courtSize, isVertical, currentSide, currentStage, selectedEvent, isEditMode, updated]);

  /**
   * Convert a TS-normalised coordinate {x,y} (0–100) to canvas pixel space.
   * Mirrors the inverse of convertToTSCoord().
   */
  const tsToCanvas = (pt, ca) => {
    if (!pt || !ca) return null;
    if (isVertical) {
      return {
        x: (pt.x / 100) * ca.width + ca.left,
        y: (pt.y / 100) * ca.height + ca.top,
      };
    } else {
      // horizontal: x_canvas maps to y_ts; y_canvas maps to (1 - x_ts)
      return {
        x: (pt.y / 100) * ca.width + ca.left,
        y: (1 - pt.x / 100) * ca.height + ca.top,
      };
    }
  };

  /**
   * Return the muted path colour for the selected event, by shot outcome:
   *   green  — aces / winners
   *   red    — errors (faults, out, netted)
   *   yellow — everything else (in play / no result)
   */
  const eventPathColour = (ev) => {
    if (!ev) return null;
    const green = "rgba(80,200,80,0.85)";
    const red = "rgba(210,70,70,0.85)";
    const yellow = "rgba(220,190,50,0.85)";
    const o = ev.outcome;
    if (ev.skill === kServing) {
      if (o === kServeAce || o === kServeWinner) return green;
      if (
        o === kServeFootfault ||
        o === kServeOut ||
        o === kServeNet ||
        o === kServeOutOffNet
      )
        return red;
      return yellow;
    }
    if (ev.skill === kReturning) {
      if (o === kReturnWinner) return green;
      if (
        o === kReturnForcingError ||
        o === kReturnOut ||
        o === kReturnUnforcedError ||
        o === kReturnNetted
      )
        return red;
      return yellow;
    }
    // rally / keypoint
    if (
      o === kRallyOutcomeWinner ||
      o === kRallyOutcomePassingShot ||
      o === kRallyOutcomeNetcord ||
      o === kRallyOutcomePutAway
    )
      return green;
    if (
      o === kRallyOutcomeOut ||
      o === kRallyOutcomeUnforcedError ||
      o === kRallyOutcomeForcingError ||
      o === kRallyOutcomeNetted ||
      o === kRallyOutcomeOutOffNet ||
      o === kRallyOutcomeNettedPassingShot ||
      o === kRallyOutcomeOutPassingShot
    )
      return red;
    return yellow;
  };

  const drawSelectedEventPath = (ctx, ca) => {
    if (!selectedEvent || !ca) return;
    const startPt = selectedEvent.start;
    const endPt = selectedEvent.end;
    if (!startPt || !endPt) return;

    const s = tsToCanvas(startPt, ca);
    const e = tsToCanvas(endPt, ca);
    if (!s || !e) return;

    const colour = eventPathColour(selectedEvent);

    // Draw dashed line shaft
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 3;
    ctx.strokeStyle = colour;
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.lineTo(e.x, e.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Arrow head at landing point
    drawArrowHead(ctx, s, e, 14, 0.35, colour, colour);

    // Small circle at hit origin
    ctx.save();
    ctx.beginPath();
    ctx.arc(s.x, s.y, 5, 0, Math.PI * 2);
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.restore();

    // Small square at landing / destination
    ctx.save();
    ctx.fillStyle = colour;
    ctx.fillRect(e.x - 4, e.y - 4, 8, 8);
    ctx.restore();
  };

  const drawBallMark = (ctx, start, end) => {
    if (start.x === 0 && start.y === 0) return;
    var colour = isBallIn ? "#00ee00" : "#ff0000";
    if (start && end && start.x !== end.x && start.y !== end.y) {
      drawArrowHead(ctx, start, end, 16, 0.3, colour, colour);
    } else {
      ctx.fillStyle = colour;
      const rad = 4;
      ctx.fillRect(end.x - rad, end.y - rad, rad * 2, rad * 2);
    }
  };

  const targetZone = (ca) => {
    if (!ca) return null;
    var tz = null;
    if (currentStage === kStageFirstServeDeuce) {
      tz =
        currentSide === 0
          ? isVertical
            ? ca.zones["deuceServeBottom"]
            : ca.zones["deuceServeRight"]
          : isVertical
          ? ca.zones["deuceServeTop"]
          : ca.zones["deuceServeLeft"];
    } else if (currentStage === kStageFirstServeAd) {
      tz =
        currentSide === 0
          ? isVertical
            ? ca.zones["adServeBottom"]
            : ca.zones["adServeRight"]
          : isVertical
          ? ca.zones["adServeTop"]
          : ca.zones["adServeLeft"];
    } else if (currentStage === kStageSecondServeDeuce) {
      tz =
        currentSide === 0
          ? isVertical
            ? ca.zones["deuceServeBottom"]
            : ca.zones["deuceServeRight"]
          : isVertical
          ? ca.zones["deuceServeTop"]
          : ca.zones["deuceServeLeft"];
    } else if (currentStage === kStageSecondServeAd) {
      tz =
        currentSide === 0
          ? isVertical
            ? ca.zones["adServeBottom"]
            : ca.zones["adServeRight"]
          : isVertical
          ? ca.zones["adServeTop"]
          : ca.zones["adServeLeft"];
    } else if (
      currentStage === kStageFirstReturnDeuce ||
      currentStage === kStageFirstReturnAd ||
      currentStage === kStageSecondReturnDeuce ||
      currentStage === kStageSecondReturnAd
    ) {
      tz =
        currentSide === 0
          ? isVertical
            ? ca.zones["rallyBottom"]
            : ca.zones["rallyRight"]
          : isVertical
          ? ca.zones["rallyTop"]
          : ca.zones["rallyLeft"];
    } else if (currentStage === kStageRally) {
      tz =
        currentSide === 0
          ? isVertical
            ? ca.zones["rallyBottom"]
            : ca.zones["rallyRight"]
          : isVertical
          ? ca.zones["rallyTop"]
          : ca.zones["rallyLeft"];
    }
    return tz;
  };

  const checkBallIsIn = (pt) => {
    var isIn = false;
    const end = convertToTSCoord(pt);
    if (currentStage === kStageFirstServeDeuce) {
      isIn =
        currentSide === 0
          ? isVertical
            ? isInZone(end, courtArea.zones["deuceServeBottom"])
            : isInZone(end, courtArea.zones["deuceServeRight"])
          : isVertical
          ? isInZone(end, courtArea.zones["deuceServeTop"])
          : isInZone(end, courtArea.zones["deuceServeLeft"]);
    } else if (currentStage === kStageFirstServeAd) {
      isIn =
        currentSide === 0
          ? isVertical
            ? isInZone(end, courtArea.zones["adServeBottom"])
            : isInZone(end, courtArea.zones["adServeRight"])
          : isVertical
          ? isInZone(end, courtArea.zones["adServeTop"])
          : isInZone(end, courtArea.zones["adServeLeft"]);
    } else if (currentStage === kStageSecondServeDeuce) {
      isIn =
        currentSide === 0
          ? isVertical
            ? isInZone(end, courtArea.zones["deuceServeBottom"])
            : isInZone(end, courtArea.zones["deuceServeRight"])
          : isVertical
          ? isInZone(end, courtArea.zones["deuceServeTop"])
          : isInZone(end, courtArea.zones["deuceServeLeft"]);
    } else if (currentStage === kStageSecondServeAd) {
      isIn =
        currentSide === 0
          ? isVertical
            ? isInZone(end, courtArea.zones["adServeBottom"])
            : isInZone(end, courtArea.zones["adServeRight"])
          : isVertical
          ? isInZone(end, courtArea.zones["adServeTop"])
          : isInZone(end, courtArea.zones["adServeLeft"]);
    } else if (
      currentStage === kStageFirstReturnDeuce ||
      currentStage === kStageFirstReturnAd ||
      currentStage === kStageSecondReturnDeuce ||
      currentStage === kStageSecondReturnAd
    ) {
      isIn =
        currentSide === 0
          ? isVertical
            ? isInZone(end, courtArea.zones["rallyBottom"])
            : isInZone(end, courtArea.zones["rallyRight"])
          : isVertical
          ? isInZone(end, courtArea.zones["rallyTop"])
          : isInZone(end, courtArea.zones["rallyLeft"]);
    } else if (currentStage === kStageRally) {
      isIn =
        currentSide === 0
          ? isVertical
            ? isInZone(end, courtArea.zones["rallyBottom"])
            : isInZone(end, courtArea.zones["rallyRight"])
          : isVertical
          ? isInZone(end, courtArea.zones["rallyTop"])
          : isInZone(end, courtArea.zones["rallyLeft"]);
    }
    setIsBallIn(isIn);
    return isIn;
  };

  // ── Where a shot may be drawn FROM ─────────────────────────────────────────
  const isServeStage = (s) =>
    s === kStageFirstServeDeuce ||
    s === kStageFirstServeAd ||
    s === kStageSecondServeDeuce ||
    s === kStageSecondServeAd;
  const isDeuceServeStage = (s) =>
    s === kStageFirstServeDeuce || s === kStageSecondServeDeuce;

  // Server's side of the court along the y-axis (0 = top baseline, 100 = bottom
  // baseline). currentSide 0 = the current player is the top player.
  const serverBaselineIsTop = () => currentSide === 0;
  // The half of the court (x < 50 vs x >= 50) the server must stand in.
  const serverIsLeftHalf = () =>
    (currentSide === 0) === isDeuceServeStage(currentStage);

  const inZoneTS = (ts, z) =>
    !!z && ts.x >= z.x && ts.x <= z.x + z.w && ts.y >= z.y && ts.y <= z.y + z.h;

  // The serve target service box in canonical TS space (matches checkBallIsIn).
  const serveTargetZoneTS = () => {
    const deuce = isDeuceServeStage(currentStage);
    if (currentSide === 0) {
      // Top server → far (bottom) service box.
      return deuce
        ? { x: 50, y: 50, w: 37, h: 27 }
        : { x: 13, y: 50, w: 37, h: 27 };
    }
    // Bottom server → far (top) service box.
    return deuce
      ? { x: 13, y: 23, w: 37, h: 27 }
      : { x: 50, y: 23, w: 37, h: 27 };
  };

  // The serve target box grown by 1 m on every side — a tap in here records the
  // serve (in, out or netted) without drawing a full line.
  const extendedServeTargetTS = () => {
    const z = serveTargetZoneTS();
    const ex = metresToTsX(CODING_RULES.serveTargetExtendM);
    const ey = metresToTsY(CODING_RULES.serveTargetExtendM);
    return { x: z.x - ex, y: z.y - ey, w: z.w + 2 * ex, h: z.h + 2 * ey };
  };

  // Where the server may stand: ~1 m behind their baseline, from the centre
  // line to the singles sideline on the correct deuce/ad half.
  const serveStartBoxTS = () => {
    const depth = metresToTsY(CODING_RULES.serveStartDepthM);
    const left = serverIsLeftHalf();
    const xMin = left ? SINGLES_LEFT_X : CENTER_X;
    const xMax = left ? CENTER_X : SINGLES_RIGHT_X;
    const yMin = serverBaselineIsTop() ? -depth : 100;
    const yMax = serverBaselineIsTop() ? 0 : 100 + depth;
    return { x: xMin, y: yMin, w: xMax - xMin, h: yMax - yMin };
  };

  // Valid serve start: from behind the baseline (drawing a line) OR a tap on the
  // extended target box.
  const isServeStartValid = (ts) =>
    inZoneTS(ts, serveStartBoxTS()) || inZoneTS(ts, extendedServeTargetTS());

  // When a serve is recorded by tapping the target, assume the serve started
  // just behind the server's baseline, 1 m from the centre line on their half.
  const assumedServeStartTS = () => {
    const behind = metresToTsY(CODING_RULES.serveTapStartBehindM);
    const oneMetre = metresToTsX(1);
    const x = serverIsLeftHalf() ? CENTER_X - oneMetre : CENTER_X + oneMetre;
    const y = serverBaselineIsTop() ? -behind : 100 + behind;
    return { x, y };
  };

  // ── Edit mode: same serve-zone geometry, derived directly from the event ──
  // Editing an existing serve must behave exactly like live coding it, but
  // can't reliably depend on the caller correctly threading through the
  // long-gone currentStage/currentSide that were live at that moment. Derive
  // deuce/ad from the event's own recorded score parity, and which physical
  // side the server stood on from its player vs the (always-current, so
  // reliable for the just-coded event) topPlayer prop.
  const serveContextForEvent = (ev) => {
    const s = ev?.scores ?? {};
    const deuce =
      ((s.player1GameScore ?? 0) + (s.player2GameScore ?? 0)) % 2 === 0;
    const serverIsTop = ev?.player?.guid === topPlayer?.guid;
    return { deuce, serverIsTop };
  };

  const serveTargetZoneForContext = ({ deuce, serverIsTop }) => {
    if (serverIsTop) {
      return deuce
        ? { x: 50, y: 50, w: 37, h: 27 }
        : { x: 13, y: 50, w: 37, h: 27 };
    }
    return deuce
      ? { x: 13, y: 23, w: 37, h: 27 }
      : { x: 50, y: 23, w: 37, h: 27 };
  };

  const extendedServeTargetForContext = (ctx) => {
    const z = serveTargetZoneForContext(ctx);
    const ex = metresToTsX(CODING_RULES.serveTargetExtendM);
    const ey = metresToTsY(CODING_RULES.serveTargetExtendM);
    return { x: z.x - ex, y: z.y - ey, w: z.w + 2 * ex, h: z.h + 2 * ey };
  };

  const assumedServeStartForContext = ({ deuce, serverIsTop }) => {
    const behind = metresToTsY(CODING_RULES.serveTapStartBehindM);
    const oneMetre = metresToTsX(1);
    // Mirrors serverIsLeftHalf()'s relationship: top+deuce or bottom+ad → left.
    const left = serverIsTop === deuce;
    const x = left ? CENTER_X - oneMetre : CENTER_X + oneMetre;
    const y = serverIsTop ? -behind : 100 + behind;
    return { x, y };
  };

  // Direction the previous shot travelled (start → end), used to orient the
  // valid return/rally start cone. Falls back to "straight away from the net".
  const prevShotAxis = () => {
    const s = selectedEvent?.start;
    const e = selectedEvent?.end;
    if (s && e) {
      const dx = e.x - s.x;
      const dy = e.y - s.y;
      const len = Math.hypot(dx, dy);
      if (len > 0.0001) return { x: dx / len, y: dy / len };
    }
    if (e) return { x: 0, y: e.y >= 50 ? 1 : -1 };
    return { x: 0, y: 1 };
  };

  // Valid return/rally start: inside a cone opening along the previous shot's
  // direction, anchored at where it landed.
  const isRallyStartValid = (ts) => {
    const prev = selectedEvent?.end;
    if (!prev) return true; // nothing to anchor to yet
    const axis = prevShotAxis();
    const vx = ts.x - prev.x;
    const vy = ts.y - prev.y;
    const proj = vx * axis.x + vy * axis.y; // component along the shot direction
    if (proj <= 0) return false; // must be past the landing point
    const dist = Math.hypot(vx, vy);
    if (dist === 0) return false;
    const cos = proj / dist; // cosine of the angle from the axis
    const half = (CODING_RULES.rallyStartHalfAngleDeg * Math.PI) / 180;
    return cos >= Math.cos(half);
  };

  const isStartValid = (ts) =>
    isServeStage(currentStage) ? isServeStartValid(ts) : isRallyStartValid(ts);

  // Fill a canonical-TS rectangle on the canvas.
  const drawZoneTS = (ctx, ca, z, fill) => {
    if (!z) return;
    const pts = [
      { x: z.x, y: z.y },
      { x: z.x + z.w, y: z.y },
      { x: z.x + z.w, y: z.y + z.h },
      { x: z.x, y: z.y + z.h },
    ].map((p) => tsToCanvas(p, ca));
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.closePath();
    ctx.fill();
  };

  // Debug overlay of the region a shot may currently be drawn from.
  const drawStartZone = (ctx, ca) => {
    if (!ca) return;
    ctx.save();
    if (isServeStage(currentStage)) {
      // Serve start box (behind the baseline) + extended target box.
      drawZoneTS(ctx, ca, serveStartBoxTS(), "rgba(0, 200, 255, 0.20)");
      drawZoneTS(ctx, ca, extendedServeTargetTS(), "rgba(255, 190, 0, 0.16)");
    } else {
      ctx.fillStyle = "rgba(0, 200, 255, 0.18)";
      const prev = selectedEvent?.end;
      if (prev) {
        const axis = prevShotAxis();
        const axisAngle = Math.atan2(axis.y, axis.x);
        const half = (CODING_RULES.rallyStartHalfAngleDeg * Math.PI) / 180;
        const R = 200; // TS units — beyond the court, canvas clips the rest
        const apex = tsToCanvas(prev, ca);
        const p1 = tsToCanvas(
          {
            x: prev.x + R * Math.cos(axisAngle - half),
            y: prev.y + R * Math.sin(axisAngle - half),
          },
          ca,
        );
        const p2 = tsToCanvas(
          {
            x: prev.x + R * Math.cos(axisAngle + half),
            y: prev.y + R * Math.sin(axisAngle + half),
          },
          ca,
        );
        ctx.beginPath();
        ctx.moveTo(apex.x, apex.y);
        ctx.lineTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.closePath();
        ctx.fill();
      }
    }
    ctx.restore();
  };

  function doEvent() {
    // var skill = 2;
    // var subskill1 = 0;
    // var subskill2 = null;
    // var outcome = null;
    // var hand = 0;
    // if (currentStage === kStageFirstServeDeuce) {
    //   skill = 0;
    //   subskill1 = 0;
    //   outcome = isBallIn ? 0 : 5;
    //   setCurrentStage(
    //     isBallIn ? kStageFirstReturnDeuce : kStageSecondServeDeuce
    //   );
    // } else if (currentStage === kStageFirstServeAd) {
    //   skill = 0;
    //   subskill1 = 0;
    //   outcome = isBallIn ? 0 : 5;
    //   setCurrentStage(isBallIn ? kStageFirstReturnAd : kStageSecondServeAd);
    // } else if (currentStage === kStageSecondServeDeuce) {
    //   skill = 0;
    //   subskill1 = 1;
    //   outcome = isBallIn ? 0 : 5;
    //   setCurrentStage(isBallIn ? kStageSecondReturnDeuce : kStageFirstServeAd);
    // } else if (currentStage === kStageSecondServeAd) {
    //   skill = 0;
    //   subskill1 = 0;
    //   outcome = isBallIn ? 0 : 5;
    //   setCurrentStage(isBallIn ? kStageSecondReturnAd : kStageFirstServeDeuce);
    // } else if (
    //   currentStage === kStageFirstReturnDeuce ||
    //   currentStage === kStageFirstReturnAd ||
    //   currentStage === kStageSecondReturnDeuce ||
    //   currentStage === kStageSecondReturnAd
    // ) {
    //   skill = 1;
    //   subskill2 = 2; // topspin
    //   outcome = isBallIn ? 0 : 1;
    //   setCurrentStage(
    //     isBallIn
    //       ? kStageRally
    //       : pointNumber % 2 !== 0
    //       ? kStageFirstServeDeuce
    //       : kStageFirstServeAd
    //   );
    // } else if (currentStage === kStageRally) {
    //   skill = 2;
    //   subskill2 = 1; // topspin
    //   outcome = isBallIn ? 0 : 2;
    //   setCurrentStage(
    //     isBallIn
    //       ? kStageRally
    //       : pointNumber % 2 !== 0
    //       ? kStageFirstServeDeuce
    //       : kStageFirstServeAd
    //   );
    // }
    // const event = {
    //   skill: skill,
    //   subskill1: subskill1,
    //   subskill2: subskill2,
    //   hand: hand,
    //   outcome: outcome,
    //   start: convertToTSCoord(start),
    //   end: convertToTSCoord(end),
    // };

    // const isIn = checkBallIsIn(end);

    // onCreateEvent(event);
    // const stage = { stage: currentStage };
    // setUndoStack([...undoStack, stage]);

    // When this was a click (not a drag), ignore a second click that lands near
    // the previous one within a short window — likely an accidental double tap.
    const DRAG_THRESHOLD = 8; // px — beyond this the gesture counts as a drag
    const DUP_DISTANCE = 25; // px — clicks closer than this are treated as dupes
    const DUP_WINDOW = 500; // ms
    const isDrag = Math.hypot(end.x - start.x, end.y - start.y) > DRAG_THRESHOLD;
    if (!isDrag) {
      const now = Date.now();
      const last = lastClickRef.current;
      if (
        last &&
        now - last.t < DUP_WINDOW &&
        Math.hypot(end.x - last.x, end.y - last.y) < DUP_DISTANCE
      ) {
        setStart({ x: 0, y: 0 });
        setEnd({ x: 0, y: 0 });
        return;
      }
      lastClickRef.current = { x: end.x, y: end.y, t: now };
    }

    // Edit mode: redraw the path of the selected (past) shot instead of coding
    // a new one. `result` is recomputed from the new end position so the page
    // can decide whether this is a simple position fix or a result change
    // requiring a safe re-code.
    if (isEditMode) {
      if (!selectedEvent) {
        setStart({ x: 0, y: 0 });
        setEnd({ x: 0, y: 0 });
        return;
      }
      const endTS = convertToTSCoord(end);
      let startTS = convertToTSCoord(start);
      let isIn;
      if (selectedEvent.skill === kServing) {
        // Serve geometry derived directly from the event being edited (see
        // serveContextForEvent) — never from currentStage/currentSide props,
        // which don't reliably reflect what was live when this shot happened.
        const ctx = serveContextForEvent(selectedEvent);
        if (inZoneTS(startTS, extendedServeTargetForContext(ctx))) {
          // Same convenience as live coding: a single tap (no drag) inside the
          // extended serve target box needs no drawn line — assume a start
          // just behind the baseline so the serve still renders as a line.
          startTS = assumedServeStartForContext(ctx);
        }
        isIn = inZoneTS(endTS, serveTargetZoneForContext(ctx));
      } else {
        isIn = checkBallIsIn(end);
      }
      const updatedEvent = {
        ...selectedEvent,
        start: startTS,
        end: endTS,
        result: isIn ? 0 : -1,
      };
      onUpdateEvent?.(updatedEvent);
      setStart({ x: 0, y: 0 });
      setEnd({ x: 0, y: 0 });
      return;
    }

    const startTS = convertToTSCoord(start);
    // Enforce where a shot may be drawn from (serve zone / rally quadrant).
    if (CODING_RULES.enforceStartZone && !isStartValid(startTS)) {
      setStart({ x: 0, y: 0 });
      setEnd({ x: 0, y: 0 });
      return;
    }

    const endTS = convertToTSCoord(end);
    // Detect a shot that finishes near the net and, from the striker's side,
    // classify it so the event editor can default the outcome:
    //   serve  → NET  (striker's side)  |  LET (crossed over)
    //   rally  → NETTED (striker's side, i.e. it never crossed)
    let netHint = null;
    if (Math.abs(endTS.y - 50) <= CODING_RULES.netProximity) {
      const strikerIsTop = currentSide === 0;
      const onStrikerSide = strikerIsTop ? endTS.y < 50 : endTS.y > 50;
      if (isServeStage(currentStage)) {
        netHint = onStrikerSide ? "net" : "let";
      } else if (onStrikerSide) {
        netHint = "netted";
      }
    }

    const isIn = checkBallIsIn(end);
    // A serve tapped into the target box has no drawn line — synthesise a start
    // just behind the baseline so the serve renders as a proper line.
    let eventStartTS = startTS;
    if (
      isServeStage(currentStage) &&
      inZoneTS(startTS, extendedServeTargetTS())
    ) {
      eventStartTS = assumedServeStartTS();
    }
    const event = {
      start: eventStartTS,
      end: endTS,
      result: isIn ? 0 : -1,
      netHint,
    };
    onCreateEvent(event);
    // Clear the transient drag marker so it doesn't paint a bright in/out arrow
    // head over the newly-selected event's path (whose head + body share the
    // muted outcome colour). The event path now represents the shot.
    setStart({ x: 0, y: 0 });
    setEnd({ x: 0, y: 0 });
  }

  function mousedown(e) {
    if (Date.now() - lastTouchRef.current < 700) return; // emulated after touch
    setIsDrawing(true);
    setStart({
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    });
    setEnd({
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    });
  }
  function mousemove(e) {
    if (!isDrawing) return;
    setEnd({
      x: e.nativeEvent.offsetX,
      y: e.nativeEvent.offsetY,
    });
  }
  function mouseup(e) {
    if (Date.now() - lastTouchRef.current < 700) return; // emulated after touch
    setIsDrawing(false);
    doEvent();
  }

  function touchstart(e) {
    lastTouchRef.current = Date.now();
    const rect = canvas.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const y = e.touches[0].clientY - rect.top;
    setIsDrawing(true);
    setStart({ x, y });
    setEnd({ x, y });
  }
  function touchmove(e) {
    if (!isDrawing) return;
    const rect = canvas.current.getBoundingClientRect();
    setEnd({
      x: e.touches[0].clientX - rect.left,
      y: e.touches[0].clientY - rect.top,
    });
  }
  function touchend(e) {
    lastTouchRef.current = Date.now();
    setIsDrawing(false);
    doEvent();
  }

  // Right-click after coding a return/rally shot is a quick way to correct
  // hand/spin without opening the event editor: a single right-click toggles
  // forehand/backhand, a second right-click landing shortly after toggles
  // topspin/slice instead. There's no native "double right-click" event, so a
  // short timer distinguishes the two — a lone click fires after the wait,
  // a follow-up click within the window cancels it and fires the spin toggle.
  const rightClickTimerRef = useRef(null);
  const lastRightClickRef = useRef(0);
  const DOUBLE_RIGHT_CLICK_WINDOW = 400; // ms

  function oncontextmenu(e) {
    e.preventDefault();
    const now = Date.now();
    const isDouble = now - lastRightClickRef.current < DOUBLE_RIGHT_CLICK_WINDOW;
    lastRightClickRef.current = now;
    if (isDouble) {
      if (rightClickTimerRef.current) {
        clearTimeout(rightClickTimerRef.current);
        rightClickTimerRef.current = null;
      }
      onToggleSpin?.();
    } else {
      rightClickTimerRef.current = setTimeout(() => {
        rightClickTimerRef.current = null;
        onToggleHand?.();
      }, DOUBLE_RIGHT_CLICK_WINDOW);
    }
  }

  const convertToTSCoord = (pt) => {
    if (isVertical) {
      return {
        x: ((pt.x - courtArea.left) / courtArea.width) * 100,
        y: ((pt.y - courtArea.top) / courtArea.height) * 100,
      };
    } else {
      return {
        y: ((pt.x - courtArea.left) / courtArea.width) * 100,
        x: (1 - (pt.y - courtArea.top) / courtArea.height) * 100,
      };
    }
  };

  return (
    <div className="CodingCourt" ref={ref}>
      <canvas
        ref={canvas}
        onMouseDown={mousedown}
        onMouseMove={mousemove}
        onMouseUp={mouseup}
        onTouchStart={touchstart}
        onTouchMove={touchmove}
        onTouchEnd={touchend}
        onContextMenu={oncontextmenu}
        width={courtSize.width}
        height={courtSize.height}
        // style={{ border: "1px solid #ff0000" }}
      ></canvas>
    </div>
  );
}
